import { CabinClass, TripType, carriersWithAlternative } from './constants';
import { ThreeColumnsItineraryType } from '@components/Results/Components/ThreeColumnsItinerary/types';
import {
  SemSegment,
  TransportType,
  TripType as GraphTripType,
  ItinerarySearchResult,
  ItineraryProduct,
  ItineraryPriceSummary,
} from 'generated/graphql';
import { buyPathMap } from 'clientState/providers/SearchRequestProvider/parsers';
import {
  MEMBER_PRICE_POLICY_DISCOUNTED,
  MEMBER_PRICE_POLICY_UNDISCOUNTED,
} from '../Shared/FeeSelector/FeeTypeName/feeTypeConstantNames';

export const hasAlternativeCarriers = (flightNumbers: string[]) => {
  return carriersWithAlternative.some((carrier) =>
    flightNumbers?.some((flightNumber) => flightNumber.includes(carrier))
  );
};

const buildFeesAndPrice = (itineraryPriceSummary: ItineraryPriceSummary) => {
  const noPrimePrice = {
    type: { id: MEMBER_PRICE_POLICY_UNDISCOUNTED },
    price: itineraryPriceSummary?.nonDiscountedPricePerPassenger
      ? itineraryPriceSummary?.nonDiscountedPricePerPassenger
      : itineraryPriceSummary?.pricePerPassenger,
  };
  const primePrice = {
    type: { id: MEMBER_PRICE_POLICY_DISCOUNTED },
    price: itineraryPriceSummary?.pricePerPassenger,
  };
  return {
    fees: [noPrimePrice, primePrice],
    price: itineraryPriceSummary.pricePerPassenger,
  };
};

export const transformItineraryStructure = (
  selectedItinerary: ItineraryProduct,
  itineraryPriceSummary: ItineraryPriceSummary
): ThreeColumnsItineraryType | null => {
  if (!selectedItinerary || !itineraryPriceSummary) {
    return null;
  }
  const feesAndPrice = buildFeesAndPrice(itineraryPriceSummary);
  const selectedItineraryStructured = {
    legs:
      selectedItinerary?.productInfo?.legs?.map((leg) => ({
        segmentKeys: [],
        segments: leg.segments.map(
          (segment) =>
            ({
              ...segment,
              carriers: [segment.sections[0].carrier],
              carrier: segment.sections[0].carrier,
              departure: segment.sections[0].departure,
              destination:
                segment.sections[segment.sections.length - 1].destination,
              departureDate: segment.sections[0].departureDate,
              arrivalDate:
                segment.sections[segment.sections.length - 1].arrivalDate,
              stops: [],
              duration: segment.duration,
              sections: segment.sections.map((section) => ({
                ...section,
                duration: section.duration,
                insuranceOffer: null,
                trips: [],
              })),
            } as SemSegment)
        ),
      })) || [],
    price: selectedItinerary?.price || feesAndPrice?.price || undefined,
    fees: feesAndPrice.fees || [],
    id: selectedItinerary?.id?.id || '0',
    ticketsLeft: 0,
    priceSortingPosition: 0,
    transportTypes: [TransportType.Plane],
  };
  return selectedItineraryStructured;
};

export const transformAlternativeItinerary = (
  alternativeItinerary,
  feeSelected,
  carriers,
  locations,
  carbonFootprints
) => {
  return {
    ...alternativeItinerary,
    __typename: 'Itinerary',
    carbonFootprint: carbonFootprints?.find(
      (carbonFootprint) =>
        carbonFootprint.id === alternativeItinerary.carbonFootprintId
    )?.carbonFootprint,
    fees: alternativeItinerary?.fees?.map((fee) => ({
      price: fee.price,
      type: { id: fee.type },
    })),
    price: alternativeItinerary?.fees?.find((fee) => fee.type === feeSelected)
      ?.price,
    legs: alternativeItinerary?.legs?.map((leg) => ({
      ...leg,
      __typename: 'Leg',
      segments: leg.segments.map((segment) => {
        const { carrierId, ...rest } = segment;
        return {
          ...rest,
          __typename: 'Segment',
          carrier: carriers.find((carrier) => carrier.id === carrierId)
            ?.carrier,
          sections: segment.sections?.map((section) => {
            const {
              departureId,
              destinationId,
              carrierId,
              operatingCarrierId,
              ...rest
            } = section;
            return {
              ...rest,
              __typename: 'Section',
              departure: locations.find(
                (location) => location.id === departureId
              )?.location,
              destination: locations.find(
                (location) => location.id === destinationId
              )?.location,
              carrier: carriers.find((carrier) => carrier.id === carrierId)
                ?.carrier,
              operatingCarrier: carriers.find(
                (carrier) => carrier.id === operatingCarrierId
              )?.carrier,
            };
          }),
        };
      }),
    })),
  };
};

export const getSearchUrl = (searchParameters): string => {
  const { host, protocol } = window.location;
  if (searchParameters) {
    const searchParams = {
      buyPath: searchParameters.buyPath,
      tripType: searchParameters.type,
      directFlightsOnly: searchParameters.directFlightsOnly,
      cabinClass: searchParameters.cabinClass,
      adults: searchParameters.numAdults,
      children: searchParameters.numChildren,
      infants: searchParameters.numInfants,
      segments: searchParameters.searchSegments,
      trainSearch: searchParameters.trainSearch,
      adultAges: searchParameters.adultAges,
      childrenAges: searchParameters.childrenAges,
      infantAges: searchParameters.infantAges,
    };
    const {
      buyPath,
      adults,
      children,
      infants,
      trainSearch,
      adultAges,
      childrenAges,
      infantAges,
    } = searchParams;
    let url = 'travel/#results/' + getCommonSearchUrl(searchParams);
    if (adults >= 1) {
      url += `;adults=${adults}`;
    }
    if (children > 0) {
      url += `;children=${children}`;
    }
    if (infants > 0) {
      url += `;infants=${infants}`;
    }
    if (buyPath) {
      url += `;buyPath=${buyPath}`;
    }

    if (trainSearch) {
      url += `;trainSearch=${trainSearch}`;

      url += getAgeQueryParams({
        adultAges,
        childrenAges,
        infantAges,
      });
    }

    return `${protocol}//${host}/${url};internalSearch=true`;
  }
  return '';
};

export const getCommonSearchUrl = (searchParams): string => {
  const { cabinClass, directFlightsOnly, tripType, segments, trainSearch } =
    searchParams;
  let url = `type=${getTripTypeSearchUrl(tripType, segments)}`;
  if (
    (tripType === TripType.ONE_WAY && segments.length === 1) ||
    tripType === TripType.ROUND_TRIP
  ) {
    const { departure, destination, dateStr } = segments[0];

    const departureAndDestination = trainSearch
      ? getRailsDepartureSearchUrl(departure) +
        getRailsDestinationSearchUrl(destination)
      : `;from=${departure.iata}${getDestinationSearchUrl(destination)}`;

    url += `${departureAndDestination};dep=${dateStr}`;

    if (tripType === TripType.ROUND_TRIP) {
      url += `;ret=${segments[1].dateStr}`;
    }
  } else {
    segments.forEach((segment, i) => {
      const { departure, destination, dateStr } = segments[i];

      const departureAndDestination = trainSearch
        ? getRailsDepartureSearchUrl(departure, i) +
          getRailsDestinationSearchUrl(destination, i)
        : `;from${i}=${departure.iata};to${i}=${destination.iata}`;

      url += `${departureAndDestination};dep${i}=${dateStr}`;
    });
  }
  if (cabinClass !== CabinClass.TOURIST) {
    url += `;class=${cabinClass}`;
  }
  if (directFlightsOnly) {
    url += ';direct=true';
  }
  return url;
};

export const getRailsDepartureSearchUrl = (
  departure,
  index?: number
): string => {
  const from = index ? `;from${index}` : ';from';
  return `${from}=${departure.geoNodeId}`;
};

export const getRailsDestinationSearchUrl = (
  destination,
  index?: number
): string => {
  const to = index ? `;to${index}` : ';to';
  return `${to}=${destination.geoNodeId}`;
};

export const getTripTypeSearchUrl = (tripType, segments): string => {
  return tripType === TripType.ONE_WAY && segments.length > 1
    ? 'M'
    : tripType[0].toUpperCase();
};

export const getDestinationSearchUrl = (destination): string => {
  let to = '';
  if (destination.geoNodeId !== 0) {
    if (destination.iata) {
      to = `;to=${destination.iata}`;
    } else {
      to = `;to=${destination.geoNodeId}`;
    }
  }
  return to;
};

const getTripType = (type) => {
  if (type === TripType.ONE_WAY) {
    return GraphTripType.OneWay;
  } else if (type === TripType.ROUND_TRIP) {
    return GraphTripType.RoundTrip;
  } else {
    return GraphTripType.MultipleDestinations;
  }
};

export const getSearchRequest = (searchParameters) => {
  const {
    buyPath,
    type,
    searchSegments,
    resident,
    numAdults,
    numChildren,
    numInfants,
  } = searchParameters;
  const requestSegments = searchSegments.map((segment) => ({
    departure: {
      geoNodeId: segment.departure.geoNodeId,
      iata: segment.departure.iata,
    },
    destination: {
      geoNodeId: segment.destination.geoNodeId,
      iata: segment.destination.iata,
    },
    date: segment.dateStr,
  }));
  const itineraryRequest = {
    segments: requestSegments,
    mainAirportsOnly: false,
    resident: !!resident,
    numAdults,
    numChildren,
    numInfants,
  };
  return {
    buyPath: buyPathMap[buyPath] || Number(buyPath),
    tripType: getTripType(type),
    itinerary: itineraryRequest,
  };
};

export const getPaxPageUrl = (isDesktop, newBookingId): string => {
  const { host, protocol, hash, search = '' } = window.location;
  const urlBase = 'travel/secure/';
  const pageName = isDesktop ? '#details' : '#passengersinfo';
  const params = new URLSearchParams(search);
  const updatedSearch =
    isDesktop && hash?.includes(`${pageName}/`)
      ? getUpdatedSearch(params)
      : search;
  return `${protocol}//${host}/${urlBase}${updatedSearch}${pageName}/${newBookingId}/`;
};

export const getUpdatedSearch = (params) => {
  if (params.has('alt')) {
    params.delete('alt');
  } else {
    params.append('alt', '1');
  }

  return params.toString() ? `?${params.toString()}` : '';
};

export const getOF1SessionState = (
  alternativeSearchId?: number,
  alternativeItinerary?: ItinerarySearchResult
) => ({
  typeOfCart: 'preShoppingCart',
  data: {
    searchId: alternativeSearchId,
    extensions: [
      {
        name: 'ESTIMATED_COLLECTION_METHOD',
        value: 'MEMBER_PRICE_POLICY_DISCOUNTED',
      },
    ],
    fareItineraryKey: alternativeItinerary?.id,
    flightNumbers: alternativeItinerary?.legs?.map((leg) =>
      leg.segmentKeys
        .map((segmentKey) => segmentKey.toString())
        .concat(leg.segments[0].sections.map((section) => section.flightCode))
        .join(',')
    ),
    segmentKeys: alternativeItinerary?.legs?.reduce<number[]>((acc, actual) => {
      return acc.concat(actual?.segmentKeys);
    }, []),
    priceWithoutDiscountSelected: false,
    membershipCouponApplied: false,
    promotedItineraryTypes: [],
  },
});

export const getAgeQueryParams = ({
  adultAges,
  childrenAges,
  infantAges,
}: {
  adultAges: string | undefined;
  childrenAges: string | undefined;
  infantAges: string | undefined;
}): string => {
  let url = '';
  if (adultAges) url += `;adultAges=${adultAges}`;
  if (childrenAges) url += `;childrenAges=${childrenAges}`;
  if (infantAges) url += `;infantAges=${infantAges}`;
  return url;
};
